"use client";

import * as React from "react";
import { Command } from "cmdk";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "../primitives/cn";

export type SearchableSelectOption = {
  value: string;
  label: string;
  /** Extra text included in search matching (e.g. email). */
  keywords?: string;
  disabled?: boolean;
};

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled,
  loading,
  className,
  onSearchChange,
  size = "default",
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  onSearchChange?: (value: string) => void;
  size?: "default" | "compact";
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const selected = options.find((opt) => opt.value === value);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled || loading}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          cn(
            "flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm transition-colors duration-micro",
            size === "compact" ? "h-9" : "h-11",
          ),
          "hover:border-[color-mix(in_srgb,hsl(var(--border))_80%,black)]",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span
          className={cn(
            "truncate text-left",
            !selected && "text-muted-foreground",
          )}
        >
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className="size-4 shrink-0 opacity-50" />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 z-[60] mt-1 overflow-hidden rounded-md border border-border bg-card text-foreground shadow-md animate-fade-in">
          <Command
            label={placeholder}
            filter={(itemValue, search) => {
              const needle = search.trim().toLowerCase();
              if (!needle) return 1;
              return itemValue.toLowerCase().includes(needle) ? 1 : 0;
            }}
          >
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <Command.Input
                autoFocus
                placeholder={searchPlaceholder}
                onValueChange={onSearchChange}
                className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <Command.List className="max-h-60 overflow-y-auto p-1">
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
                {emptyText}
              </Command.Empty>
              {options.map((opt) => (
                <Command.Item
                  key={opt.value}
                  value={`${opt.label} ${opt.keywords ?? ""} ${opt.value}`}
                  disabled={opt.disabled}
                  // preventDefault: stop parent <label> from re-clicking the trigger
                  // (which would toggle the list open again after we close it).
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() => {
                    if (opt.disabled) return;
                    onValueChange(opt.value);
                    setOpen(false);
                  }}
                  className={cn(
                    "relative flex cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none",
                    "aria-selected:bg-accent data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
                  )}
                >
                  <span className="absolute left-2 flex size-3.5 items-center justify-center">
                    {value === opt.value ? <Check className="size-4" /> : null}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </div>
      ) : null}
    </div>
  );
}
